import { selectPerpsOnlyStatus, type PerpsOnlyStatus } from 'lib/perpsOnlyStatus';
import { useAuthStore } from 'lib/stores/authStore';
import type { DepositDestination } from 'models/reqres';

export type FundingAccount = 'event_contract' | 'margined';

const DESTINATION_AWARE_DEPOSIT_METHODS = new Set([
    'apple_pay',
    'bank',
    'card',
    'cashapp',
    'google_pay',
    'paypal',
    'venmo',
]);

// Crypto carries no destination on the request. Only Coinbase deposits get a
// lane resolved on arrival (coinbaseDepositBookAccount); Stripe crypto dead-ends
// for Perps-only accounts and ZeroHash books to event-contract, so both stay out.
const PERPS_ONLY_CRYPTO_VENDOR = 'coinbase';

// `FundMethods` stores provider families rather than the public rail names
// above (Apple Pay and Google Pay, for example, both persist as
// `debit_card`). Keep this list default-deny so a newly persisted,
// destinationless method cannot be resumed for a Perps-only account before it
// is explicitly audited.
const DESTINATION_AWARE_PERSISTED_FUND_METHODS = new Set([
    'bank',
    'braintree_paypal',
    'braintree_venmo',
    'cashapp',
    'debit_card',
]);

const DEPOSIT_DESTINATIONS: Record<FundingAccount, DepositDestination> = {
    event_contract: 'event_contract',
    margined: 'margin',
};

export class FundingAccountPolicyError extends Error {
    readonly code: 'perps_only_transfer_direction';

    constructor(code: FundingAccountPolicyError['code']) {
        super('This account can only move Predictions funds to Perpetuals.');
        this.name = 'FundingAccountPolicyError';
        this.code = code;
    }
}

export type FundingAccountPolicyResult<T> = { ok: true; value: T } | { ok: false; error: FundingAccountPolicyError };

export function getFundingAccountStatus(): PerpsOnlyStatus {
    return selectPerpsOnlyStatus(useAuthStore.getState());
}

export function getFundingAccountTrackingFields(
    requestedAccount: FundingAccount,
    status: PerpsOnlyStatus = getFundingAccountStatus(),
): {
    account_mode: 'standard' | 'perps_only' | 'unknown';
    resolved_destination: DepositDestination | 'unknown';
} {
    return {
        account_mode: status === 'perpsOnly' ? 'perps_only' : 'standard',
        resolved_destination: getEffectiveDepositDestination(requestedAccount, status),
    };
}

/**
 * Resolves the destination at the request boundary. Presentation state is not
 * trusted because it can outlive an address-based account reclassification.
 */
export function getEffectiveDepositDestination(
    requestedAccount: FundingAccount,
    status: PerpsOnlyStatus = getFundingAccountStatus(),
): DepositDestination {
    const result = resolveEffectiveDepositDestination(requestedAccount, status);
    if (!result.ok) {
        throw result.error;
    }

    return result.value;
}

/**
 * UI submission handlers use this non-throwing form before setting pending
 * state. The throwing wrapper above remains the final request-boundary
 * assertion for callers that cannot proceed without a resolved destination.
 */
export function resolveEffectiveDepositDestination(
    requestedAccount: FundingAccount,
    status: PerpsOnlyStatus = getFundingAccountStatus(),
): FundingAccountPolicyResult<DepositDestination> {
    return {
        ok: true,
        value: status === 'perpsOnly' ? 'margin' : DEPOSIT_DESTINATIONS[requestedAccount],
    };
}

export type PerpsOnlyMethodContext = {
    cryptoVendor?: string | null;
};

/**
 * New or destinationless rails stay unavailable to Perps-only accounts until
 * the frontend can explicitly include a Perpetuals destination in the request,
 * or the resolved vendor books one on arrival.
 */
export function isDepositMethodAvailableForAccount(
    method: string,
    status: PerpsOnlyStatus,
    context: PerpsOnlyMethodContext = {},
): boolean {
    if (status !== 'perpsOnly') return true;
    if (method === 'crypto') return context.cryptoVendor === PERPS_ONLY_CRYPTO_VENDOR;
    return DESTINATION_AWARE_DEPOSIT_METHODS.has(method);
}

export function isUnsupportedPersistedPerpsOnlyFundMethod(
    method: string | undefined,
    context: PerpsOnlyMethodContext = {},
): boolean {
    if (method === undefined) return false;
    if (method === 'crypto') return context.cryptoVendor !== PERPS_ONLY_CRYPTO_VENDOR;
    return !DESTINATION_AWARE_PERSISTED_FUND_METHODS.has(method);
}

export function shouldRestartPerpsOnlyDeposit(params: {
    status: PerpsOnlyStatus;
    requestedAccount: FundingAccount;
    hasResumableState: boolean;
    isUnsupportedResume: boolean;
}): boolean {
    const { status, requestedAccount, hasResumableState, isUnsupportedResume } = params;
    return status === 'perpsOnly' && hasResumableState && (requestedAccount !== 'margined' || isUnsupportedResume);
}

export function assertIntraExchangeTransferAllowed<T extends { source: FundingAccount; destination: FundingAccount }>(
    request: T,
    status: PerpsOnlyStatus = getFundingAccountStatus(),
): T {
    if (status === 'perpsOnly' && (request.source !== 'event_contract' || request.destination !== 'margined')) {
        throw new FundingAccountPolicyError('perps_only_transfer_direction');
    }

    return request;
}
